<!-- MpfForm.vue -->
<script setup lang="ts">
  import { computed, reactive, watch } from 'vue';
  import {
    MpfFormProps,
    MpfFormBlockConfig,
    MpfFormFieldConfig,
    mpfFormDefaults,
    FormFieldValue,
    isInputField,
    isMpfInputTextType,
  } from './MpfForm.props';

  import {
    useValidation,
    validateBloodPressureField,
    validateDateField,
    validateTextField,
  } from '../../composables/useValidation';
  import MpfInputText from '../MpfInputText/MpfInputText.vue';
  import MpfRadios from '../MpfRadios/MpfRadios.vue';
  import './MpfForm.scss';
  import MpfCheckboxes from '../MpfCheckboxes/MpfCheckboxes.vue';
  import MpfInputDate from '../MpfInputDate/MpfInputDate.vue';
  import MpfHint from '../MpfHint/MpfHint.vue';
  import MpfButton from '../MpfButton/MpfButton.vue';
  import MpfInputTextarea from '../MpfInputTextarea/MpfInputTextarea.vue';
  import MpfAttachments from '../MpfAttachments/MpfAttachments.vue';
  import MpfContentRenderer from '../MpfContentRenderer/MpfContentRenderer.vue';
  import MpfNotificationBanner from '../MpfNotificationBanner/MpfNotificationBanner.vue';
  import MpfSelect from '../MpfSelect/MpfSelect.vue';
  import MpfInputBloodPressure from '../MpfInputBloodPressure/MpfInputBloodPressure.vue';

  const props = withDefaults(defineProps<MpfFormProps>(), mpfFormDefaults);

  const emit = defineEmits<{
    (e: 'submit', payload: { values: Record<string, FormFieldValue>; valid: boolean }): void;
    (e: 'change', payload: { values: Record<string, FormFieldValue> }): void;
  }>();

  const { validateAll } = useValidation();

  const fieldValues = reactive<Record<string, FormFieldValue>>({});
  const fieldEverHadValue = reactive<Record<string, boolean>>({});

  type FieldErrorEntry = { name: string; label: string; error: string; global?: boolean };

  const fieldErrors = reactive<Record<string, FieldErrorEntry | null>>({});

  const displayErrorEntries = computed(() =>
    Object.values(fieldErrors).filter(
      (entry): entry is FieldErrorEntry => !!entry && !!entry.error,
    ),
  );

  const isTextField = (field: MpfFormFieldConfig): boolean => isMpfInputTextType(field.type);

  const inputFields = computed(() => props.fields.filter(isInputField));

  // Content blocks have no name to key on
  const blockKey = (block: MpfFormBlockConfig, index: number) =>
    isInputField(block) ? block.name : `${block.type}-${index}`;

  const headingTag = computed(() =>
    props.headingLevel ? (`h${props.headingLevel}` as keyof HTMLElementTagNameMap) : 'div',
  );
  const headingSize = computed(() =>
    props.headingLevelSize ? `mpf-fieldset__legend--${props.headingLevelSize}` : null,
  );

  function normaliseBackendErrors(list) {
    const map = {};
    list.forEach((err) => {
      const key = err.name;
      const val = Array.isArray(err.errors) ? err.errors : [err.errors];
      map[key] = val;
    });
    return map;
  }

  const hasErrors = computed(() => displayErrorEntries.value.length > 0);

  function initFields(fields: MpfFormFieldConfig[]) {
    fields.forEach((field) => {
      if (!(field.name in fieldValues)) {
        if (field.type === 'checkboxes') {
          // checkbox groups always use an array
          if (Array.isArray(field.initialValue)) {
            fieldValues[field.name] = field.initialValue;
          } else if (field.initialValue != null) {
            fieldValues[field.name] = [field.initialValue];
          } else {
            fieldValues[field.name] = [];
          }
        } else if (field.type === 'radios' || field.type === 'select') {
          // radios and selects use a single primitive value
          fieldValues[field.name] = field.initialValue !== undefined ? field.initialValue : null;
        } else if (field.type === 'attachments' && !field.initialValue) {
          fieldValues[field.name] = Array.isArray(field.initialValue) ? field.initialValue : [];
        } else {
          fieldValues[field.name] = field.initialValue ?? '';
        }
      }

      if (!(field.name in fieldErrors)) {
        fieldErrors[field.name] = null;
      }

      if (!(field.name in fieldEverHadValue)) {
        if (field.type === 'checkboxes') {
          const v = fieldValues[field.name];
          fieldEverHadValue[field.name] = Array.isArray(v) && v.length > 0;
        } else if (field.type === 'radios' || field.type === 'select') {
          fieldEverHadValue[field.name] =
            fieldValues[field.name] != null && fieldValues[field.name] !== '';
        } else {
          fieldEverHadValue[field.name] = false;
        }
      }
    });

    Object.keys(fieldValues).forEach((name) => {
      if (!fields.some((f) => f.name === name)) {
        delete fieldValues[name];
        delete fieldErrors[name];
        delete fieldEverHadValue[name];
      }
    });
  }

  function markFieldHadValue(field: MpfFormFieldConfig) {
    const value = fieldValues[field.name];
    let has = false;

    if (field.type === 'checkboxes') {
      has = Array.isArray(value) && value.length > 0;
    } else if (
      field.type === 'radios' ||
      field.type === 'date' ||
      field.type === 'text' ||
      field.type === 'select' ||
      field.type === 'bloodPressure'
    ) {
      const trimmed = typeof value === 'string' ? value.trim() : '';
      has = !!trimmed;
    }

    if (has) {
      fieldEverHadValue[field.name] = true;
    }
  }

  watch(
    () => props.fields,
    () => {
      initFields(inputFields.value);
    },
    { immediate: true, deep: true },
  );

  watch(
    fieldValues,

    (newValues) => {
      emit('change', { values: { ...newValues } });
    },

    { deep: true },
  );

  function handleChange() {
    const valuesCopy: Record<string, FormFieldValue> = {};

    Object.keys(fieldValues).forEach((key) => {
      valuesCopy[key] = fieldValues[key];
    });
    emit('change', { values: valuesCopy });
  }

  watch(
    () => props.formErrors,
    (beErrors) => {
      if (!beErrors || beErrors.length === 0) return;

      // Convert BE list → map
      const backendMap = normaliseBackendErrors(beErrors);

      Object.keys(backendMap).forEach((name) => {
        const messages = backendMap[name];

        // Try to locate matching field so we can inject label
        const field = inputFields.value.find((f) => f.name === name);

        if (!field) {
          // A rejection the server did not tie to a question has no field to sit
          // under. Show it as a form-level line rather than losing it silently.
          fieldErrors[name] = {
            name,
            label: '',
            error: messages.join(', '),
            global: true,
          };
          return;
        }

        // Replace FE error with BE error
        fieldErrors[name] = {
          name,
          label: field.label,
          error: messages.join(', '),
        };
      });
    },
    { immediate: true, deep: true },
  );

  function validateSingleField(field: MpfFormFieldConfig) {
    handleChange();

    const name = field.name;
    const value = fieldValues[name];
    const hadValue = fieldEverHadValue[name] ?? false;

    // ---------- CHECKBOXES ----------
    if (field.type === 'checkboxes') {
      const arr = Array.isArray(value) ? value : [];

      // untouched + empty → do nothing
      if (!hadValue && arr.length === 0) {
        return;
      }

      // required but empty
      if (field.required && arr.length === 0) {
        fieldErrors[name] = {
          name,
          label: field.label,
          error: 'Please select at least one option.',
        };
      } else {
        fieldErrors[name] = null;
      }
      return;
    }
    if (field.type === 'attachments') {
      fieldValues[field.name] = [];
      return;
    }

    // ---------- RADIOS ----------
    if (field.type === 'radios') {
      const selected = value ?? '';

      // untouched + empty → no validation yet
      if (!hadValue && !selected) {
        return;
      }

      if (!selected && field.required) {
        fieldErrors[name] = {
          name,
          label: field.label,
          error: 'Please choose an option.',
        };
      } else {
        fieldErrors[name] = null;
      }
      return;
    }

    // ---------- SELECT ----------
    if (field.type === 'select') {
      const selected = value ?? '';

      if (!hadValue && !selected) {
        return;
      }

      fieldErrors[name] =
        !selected && field.required
          ? { name, label: field.label, error: 'Please choose an option.' }
          : null;
      return;
    }

    // ---------- BLOOD PRESSURE ----------
    if (field.type === 'bloodPressure') {
      const error = validateBloodPressureField(field, typeof value === 'string' ? value : '');

      if (!error && !hadValue) {
        fieldErrors[name] = null;
        return;
      }

      fieldErrors[name] = error ? { name, label: field.label, error } : null;
      return;
    }

    // ---------- DATE ----------
    if (field.type === 'date' || field.type === 'dateOfBirth') {
      const dateStr = typeof value === 'string' ? value.trim() : '';

      // untouched + empty → skip validation
      if (!dateStr && !hadValue) {
        return;
      }

      const errorMsg = validateDateField(field, dateStr);

      fieldErrors[name] = errorMsg ? { name, label: field.label, error: errorMsg } : null;

      return;
    }

    // ---------- TEXT INPUTS ----------
    const trimmed = typeof value === 'string' ? value.trim() : '';

    // untouched + empty → skip
    if (!trimmed && !hadValue) {
      return;
    }

    // required + empty
    if (!trimmed && field.required) {
      fieldErrors[name] = {
        name,
        label: field.label,
        error: 'This field is required.',
      };
      return;
    }

    // optional + empty → clear error
    if (!trimmed && !field.required) {
      fieldErrors[name] = null;
      return;
    }

    // custom validator
    const error = validateTextField(field, trimmed);

    fieldErrors[name] = error ? { name, label: field.label, error } : null;
  }

  function validateAndSetErrors(): boolean {
    const { errors, valid } = validateAll(props.fields, fieldValues);

    for (const key in fieldErrors) {
      delete fieldErrors[key];
    }

    inputFields.value.forEach((field) => {
      const err = errors[field.name] ?? '';
      fieldErrors[field.name] = err ? { name: field.name, label: field.label, error: err } : null;
    });

    return valid;
  }

  function handleSubmit() {
    const valid = validateAndSetErrors();

    const valuesCopy: Record<string, FormFieldValue> = {};

    Object.keys(fieldValues).forEach((key) => {
      valuesCopy[key] = fieldValues[key];
    });

    emit('submit', { values: valuesCopy, valid });
  }
</script>

<template>
  <form class="mpf-form" @submit.prevent="handleSubmit" novalidate>
    <fieldset v-if="formTitle" class="mpf-fieldset">
      <legend class="mpf-fieldset__legend" :class="headingSize">
        <component :is="headingTag" class="mpf-fieldset__heading">
          {{ formTitle }}
        </component>
      </legend>
    </fieldset>
    <mpf-hint v-if="props.hint" :hint="props.hint" />

    <div
      v-if="displayErrorEntries.length || showErrors"
      class="mpf-error-summary"
      data-module="mpf-error-summary"
    >
      <div role="alert">
        <h2 class="mpf-error-summary__title">{{ errorSummaryTitle }}</h2>
        <div class="mpf-error-summary__body">
          <p>{{ errorSummaryByLine }}</p>
          <slot name="error-content" />
          <ul v-if="displayErrorEntries.length" class="mpf-list mpf-error-summary__list">
            <li v-for="entry in displayErrorEntries" :key="entry.name">
              <span v-if="entry.global">{{ entry.error }}</span>
              <a v-else :href="`#mpf-form-${entry.name}-error`">
                <!-- A field with no visible label (a lone confirmation checkbox) would
                     otherwise render a dangling "– message". -->
                <template v-if="entry.label">{{ entry.label }} – </template>{{ entry.error }}
              </a>
            </li>
          </ul>
        </div>
      </div>
    </div>

    <div class="mpf-form__fields">
      <template
        v-for="(field, index) in props.fields"
        :key="blockKey(field, index)"
      >
        <hr v-if="field.separator" class="mpf-form__separator" />
        <div
          class="mpf-form__field"
          :class="{ 'mpf-form__field--banner': field.type === 'infoBanner' }"
        >
          <template v-if="isInputField(field)">
            <template v-if="field.type === 'radios'">
              <MpfRadios
                v-model:modelValue="fieldValues[field.name] as string | number | null"
                :id="`mpf-form-${field.name}`"
                :name="field.name"
                :label="field.label"
                :legendHeadingSize="field.legendHeadingSize"
                :legend-heading-level="field.legendHeadingLevel"
                :hint="field.hint"
                :srLabel="field.srLabel"
                :srHint="field.srHint"
                :required="field.required"
                :items="field.items || []"
                :errors="fieldErrors[field.name] ? [fieldErrors[field.name].error] : []"
                @blur="() => validateSingleField(field)"
                @change="() => validateSingleField(field)"
                @input="() => markFieldHadValue(field)"
              />
            </template>

            <template v-if="field.type === 'checkboxes' && field.items.length">
              <MpfCheckboxes
                v-model:modelValue="fieldValues[field.name] as (string | number | null)[]"
                :id="`mpf-form-${field.name}`"
                :name="field.name"
                :label="field.label"
                :legendHeadingSize="field.legendHeadingSize"
                :legend-heading-level="field.legendHeadingLevel"
                :hint="field.hint"
                :srLabel="field.srLabel"
                :srHint="field.srHint"
                :required="field.required"
                :items="field.items || []"
                :errors="fieldErrors[field.name] ? [fieldErrors[field.name].error] : []"
                @blur="() => validateSingleField(field)"
                @change="
                  () => {
                    markFieldHadValue(field);
                    validateSingleField(field);
                  }
                "
                @input="() => markFieldHadValue(field)"
              />
            </template>

            <template v-if="field.type === 'dateOfBirth' || field.type === 'date'">
              <MpfInputDate
                v-model:modelValue="fieldValues[field.name] as string"
                :id="`mpf-form-${field.name}`"
                :name="field.name"
                :label="field.label"
                :legendHeadingSize="field.legendHeadingSize"
                :legend-font-weight="field.legendFontWeight"
                :hint="field.hint"
                :srLabel="field.srLabel"
                :srHint="field.srHint"
                :required="field.required"
                :errors="fieldErrors[field.name] ? [fieldErrors[field.name].error] : []"
                @blur="() => validateSingleField(field)"
                @change="() => validateSingleField(field)"
                @input="() => markFieldHadValue(field)"
              />
            </template>

            <template v-if="field.type === 'attachments'">
              <MpfAttachments
                :model-value="(fieldValues[field.name] ?? []) as File[]"
                :id="`mpf-form-${field.name}`"
                v-bind="field"
                :name="field.name"
                :label="field.label"
                :hint="field.hint"
                :srLabel="field.srLabel"
                :srHint="field.srHint"
                :required="field.required"
                :errors="fieldErrors[field.name] ? [fieldErrors[field.name].error] : []"
                @update:model-value="
                  (value) => {
                    fieldValues[field.name] = value;
                    handleChange();
                  }
                "
                @blur="() => validateSingleField(field)"
                @change="() => validateSingleField(field)"
                @input="() => markFieldHadValue(field)"
                :initial-value="(fieldValues[field.name] ?? []) as File[]"
                :multiple="field.multiple"
                :heading-level-size="field.headingLevelSize"
                :heading-level="field.headingLevel"
              />
            </template>

            <template v-if="field.type === 'select'">
              <MpfSelect
                v-model:modelValue="fieldValues[field.name] as string | number | null"
                :id="`mpf-form-${field.name}`"
                :name="field.name"
                :label="field.label"
                :hint="field.hint"
                :srLabel="field.srLabel"
                :srHint="field.srHint"
                :required="field.required"
                :items="field.items || []"
                :include-empty-option="field.includeEmptyOption"
                :empty-option-label="field.emptyOptionLabel"
                :heading-level-size="field.headingLevelSize"
                :errors="fieldErrors[field.name] ? [fieldErrors[field.name].error] : []"
                @blur="() => validateSingleField(field)"
                @change="
                  () => {
                    markFieldHadValue(field);
                    validateSingleField(field);
                  }
                "
              />
            </template>

            <template v-if="field.type === 'bloodPressure'">
              <MpfInputBloodPressure
                v-model:modelValue="fieldValues[field.name] as string"
                :id="`mpf-form-${field.name}`"
                :name="field.name"
                :label="field.label"
                :legendHeadingSize="field.legendHeadingSize"
                :legend-heading-level="field.legendHeadingLevel"
                :legend-font-weight="field.legendFontWeight"
                :hint="field.hint"
                :srLabel="field.srLabel"
                :srHint="field.srHint"
                :required="field.required"
                :systolic-label="field.systolicLabel"
                :diastolic-label="field.diastolicLabel"
                :errors="fieldErrors[field.name] ? [fieldErrors[field.name].error] : []"
                @blur="() => validateSingleField(field)"
                @change="() => validateSingleField(field)"
                @input="() => markFieldHadValue(field)"
              />
            </template>

            <template v-else-if="isTextField(field)">
              <MpfInputText
                v-model:modelValue="fieldValues[field.name] as string"
                :id="`mpf-form-${field.name}`"
                :name="field.name"
                :type="field.type"
                :label="field.label"
                :hint="field.hint"
                :srLabel="field.srLabel"
                :srHint="field.srHint"
                :required="field.required"
                :heading-level-size="field.headingLevelSize"
                :errors="fieldErrors[field.name] ? [fieldErrors[field.name].error] : []"
                :ariaDescribedby="''"
                @blur="() => validateSingleField(field)"
                @input="() => markFieldHadValue(field)"
              />
            </template>

            <template v-else-if="field.type === 'textarea'">
              <MpfInputTextarea
                v-model:modelValue="fieldValues[field.name] as string"
                :id="`mpf-form-${field.name}`"
                v-bind="field"
                :name="field.name"
                :label="field.label"
                :hint="field.hint"
                :srLabel="field.srLabel"
                :srHint="field.srHint"
                :required="field.required"
                :errors="fieldErrors[field.name] ? [fieldErrors[field.name].error] : []"
                @blur="() => validateSingleField(field)"
                @input="() => markFieldHadValue(field)"
              />
            </template>
          </template>

          <template v-else-if="field.type === 'description'">
            <MpfContentRenderer :content-data="field.content" />
          </template>

          <template v-else-if="field.type === 'infoBanner'">
            <MpfNotificationBanner :heading="field.heading">
              <MpfContentRenderer v-if="field.content" :content-data="field.content" />
              <p v-else class="mpf-u-margin-bottom-0">{{ field.text }}</p>
            </MpfNotificationBanner>
          </template>
        </div>
      </template>
    </div>

    <div class="mpf-form__actions">
      <!-- NB. Prevent double click with 600ms on by default -->
      <mpf-button
        :label="props.submitLabel"
        :variant="props.submitVariant"
        type="submit"
        @click="handleSubmit"
        prevent-double-click
      />

      <p v-if="hasErrors" class="mpf-form__summary">
        Please fix the highlighted fields before submitting.
      </p>
    </div>
  </form>
</template>
